import { useState, useEffect } from "react";

const CarouselComponent = ({photos}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImageIndex, setModalImageIndex] = useState(0);

    useEffect(() => {
        if (!photos || photos.length <= 1) return;
        
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => 
                prevIndex === photos.length - 1 ? 0 : prevIndex + 1
            );
        }, 4000);

        return () => clearInterval(interval);
    }, [photos]);

    // Handle escape key to close modal and navbar visibility
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsModalOpen(false);
            }
        };

        if (isModalOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
            // Hide navbar when modal is open
            document.body.classList.add('modal-open');
        } else {
            document.body.style.overflow = 'unset';
            // Show navbar when modal is closed
            document.body.classList.remove('modal-open');
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
        };
    }, [isModalOpen]);

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const goToPrevious = () => {
        setCurrentIndex(currentIndex === 0 ? photos.length - 1 : currentIndex - 1);
    };

    const goToNext = () => {
        setCurrentIndex(currentIndex === photos.length - 1 ? 0 : currentIndex + 1);
    };

    const openModal = (index) => {
        setModalImageIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const goToModalPrevious = () => {
        setModalImageIndex(modalImageIndex === 0 ? photos.length - 1 : modalImageIndex - 1);
    };

    const goToModalNext = () => {
        setModalImageIndex(modalImageIndex === photos.length - 1 ? 0 : modalImageIndex + 1);
    };

    if (!photos || photos.length === 0) {
        return (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-8 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-8 bg-slate-900 dark:bg-slate-400 rounded-full"></div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white transition-colors duration-300">
                        Project Gallery
                    </h2>
                </div>
                <div className="text-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p className="text-slate-500 font-medium">No gallery images available</p>
                </div>
            </div>
        );
    }

    return(
        <>
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="space-y-6">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-8 bg-slate-900 dark:bg-slate-400 rounded-full"></div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white transition-colors duration-300">
                            Project Gallery
                        </h2>
                    </div>

                    <div className="relative group">
                        {/* Main carousel container */}
                        <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden rounded-2xl bg-slate-100 cursor-pointer">
                            {photos.map((photo, index) => (
                                <div
                                    key={index}
                                    className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                                        index === currentIndex ? 'translate-x-0' : 
                                        index < currentIndex ? '-translate-x-full' : 'translate-x-full'
                                    }`}
                                    onClick={() => openModal(currentIndex)}
                                >
                                    <img
                                        src={photo.photoUrl}
                                        alt={`Project screenshot ${index + 1}`}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    />
                                    {/* Click to view overlay */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Gradient overlays for navigation */}
                            <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                        </div>

                        {/* Navigation arrows */}
                        {photos.length > 1 && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        goToPrevious();
                                    }}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-white hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 z-10"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        goToNext();
                                    }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-white hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 z-10"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </>
                        )}

                        {/* Slide indicators */}
                        {photos.length > 1 && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                                {photos.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            goToSlide(index);
                                        }}
                                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                            index === currentIndex 
                                                ? 'bg-white scale-110' 
                                                : 'bg-white/50 hover:bg-white/70'
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Image counter and view all button */}
                    <div className="flex items-center justify-between">
                        {photos.length > 1 && (
                            <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {currentIndex + 1} of {photos.length}
                            </span>
                        )}
                        
                        <button
                            onClick={() => openModal(currentIndex)}
                            className="inline-flex items-center px-4 py-2 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-white text-white dark:text-slate-900 text-sm font-semibold rounded-full transition-all duration-300 hover:scale-105 shadow-sm"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                            View Full Size
                        </button>
                    </div>

                    {/* Thumbnail strip */}
                    {photos.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                            {photos.map((photo, index) => (
                                <button
                                    key={index}
                                    onClick={() => openModal(index)}
                                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
                                        index === currentIndex 
                                            ? 'border-slate-800 dark:border-slate-200 ring-2 ring-slate-400 dark:ring-slate-500' 
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
                                    }`}
                                >
                                    <img
                                        src={photo.photoUrl}
                                        alt={`Thumbnail ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div 
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
                    onClick={closeModal}
                >
                    {/* Close button - positioned relative to the entire modal */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            closeModal();
                        }}
                        className="absolute top-4 right-4 z-20 p-4 bg-red-500/80 backdrop-blur-sm border-2 border-white/60 rounded-full text-white hover:bg-red-600/80 transition-all duration-300 hover:scale-110 shadow-lg"
                        style={{ minWidth: '56px', minHeight: '56px', touchAction: 'manipulation' }}
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Modal image container - centered and sized to content */}
                    <div className="flex items-center justify-center min-h-screen p-4">
                        <div 
                            className="relative max-w-full max-h-screen"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={photos[modalImageIndex].photoUrl}
                                alt={`Project screenshot ${modalImageIndex + 1}`}
                                className="max-w-full max-h-screen object-contain rounded-lg shadow-2xl"
                            />
                        </div>
                    </div>

                    {/* Modal navigation - positioned relative to the entire modal */}
                    {photos.length > 1 && (
                        <>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    goToModalPrevious();
                                }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-white/30 backdrop-blur-sm border border-white/40 rounded-full text-white hover:bg-white/40 transition-all duration-300 hover:scale-110 shadow-lg"
                                style={{ minWidth: '56px', minHeight: '56px', touchAction: 'manipulation' }}
                            >
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    goToModalNext();
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-white/30 backdrop-blur-sm border border-white/40 rounded-full text-white hover:bg-white/40 transition-all duration-300 hover:scale-110 shadow-lg"
                                style={{ minWidth: '56px', minHeight: '56px', touchAction: 'manipulation' }}
                            >
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </>
                    )}

                    {/* Modal indicators - positioned relative to the entire modal */}
                    {photos.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/50 backdrop-blur-sm rounded-full px-6 py-3">
                            <div className="flex space-x-3">
                                {photos.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setModalImageIndex(index);
                                        }}
                                        className={`w-4 h-4 rounded-full transition-all duration-300 ${
                                            index === modalImageIndex 
                                                ? 'bg-white scale-125' 
                                                : 'bg-white/50 hover:bg-white/75'
                                        }`}
                                        style={{ minWidth: '32px', minHeight: '32px', touchAction: 'manipulation' }}
                                    />
                                ))}
                            </div>
                            <span className="text-white text-sm font-medium">
                                {modalImageIndex + 1} / {photos.length}
                            </span>
                        </div>
                    )}

                    {/* Enhanced mobile close hint */}
                    <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white text-base px-6 py-3 rounded-full opacity-80 md:hidden animate-pulse">
                        ← Tap the red ✕ or tap outside to close
                    </div>
                </div>
            )}
        </>
    )
}
export default CarouselComponent;
